/**
 * Created by Ning Jiang on 2021-06-28.
 */

import {ContinueScreen} from "../../core/screen/ContinueScreen";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {Layers, SerialLayers} from "../view/SerialLayers";
import {Serial} from "../Serial";
import {gsap} from "gsap";
import {SerialSound} from "../sound/SerialSound";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {HiddenEnhancerReelController} from "../reels/HiddenEnhancerReelController";
import {Translation} from "../../core/utils/Translation";

export class FreespinIntro extends ContinueScreen {

    private _text:PIXI.Text;
    private _spine:PIXI.spine.Spine;
    private _spineBg:PIXI.spine.Spine;
    private _animationContainer:PIXI.Container;

    private _dimmerLooping:boolean = false;
    private _dimmerTl:gsap.core.Timeline | null;

    constructor() {
        super(SerialLayers.getLayer(Layers.SCREENS)!);

        this._animationContainer = new PIXI.Container();
        this._container.addChild(this._animationContainer);

        this._text = new PIXI.Text("", new PIXI.TextStyle({
            dropShadow : true,
            dropShadowBlur : 2,
            dropShadowDistance : 2,
            fill : 0xff0000,
            fontFamily : OpenSans.FAMILY,
            fontWeight : FontWeight.BOLD,
            lineJoin : "round",
            fontSize : 50,
            wordWrap : false
        }));
        this._text.anchor.set(0.5, 0.5);
        this._text.position.set(360, 360);
        this._text.alpha = 0;
        this._container.addChild(this._text);
    }

    public show(onContinueCallBack:() => void):void {
        SlotGame.keypadPlugin.hide();
        if(Serial.model.data.nextMode.includes("SUPER")) {
            Serial.jumpingScatterController.init();
        }
        const restore = Serial.model.isRestore;
        const fsLeft = restore ? Serial.model.parsedInitData.freespinsLeft : Serial.model.parsedGameData.freespinsLeft;
        this._text.text = `${fsLeft} ${Translation.translation(`SPIN${fsLeft === 1 ? "" : "S"}`)}`;
        this._text.style.fill = Serial.model.data.nextMode.includes("SUPER") ? 0xff0200 : 0xecda1d;
        this._text.y = Serial.model.data.nextMode.includes("SUPER") ? 360 : 420;

        this._spine = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset(`${Serial.model.data.nextMode.includes("SUPER") ? "s" : ""}fsIntro`));
        this._spine.autoUpdate = true;
        this._spine.position.set(360, 360);
        this._spine.visible = false;
        this._animationContainer.addChild(this._spine);

        if(Serial.model.data.nextMode.includes("SUPER")) {
            this._spineBg = new PIXI.spine.Spine(Serial.assetLoader.getSpineAsset(`bg`));
            this._spineBg.autoUpdate = true;
            this._spineBg.position.set(360, 360);
            this._spineBg.visible = false;
            this._animationContainer.addChild(this._spineBg);
        }

        const tl = gsap.timeline();
        tl.add([
            () => {
                if(Serial.model.data.nextMode.includes("SUPER")) {
                    this._spineBg.visible = true;
                    this._spineBg.state.setAnimation(0, "sfs", true);
                    this._dimmerLooping = true;
                    this.playDimmerLoop();
                } else {
                    Serial.background.tweenDimmer(0.8, restore ? 0 : 0.4);
                }
                this._spine.visible = true;
                this._spine.state.setAnimation(0, "Start", false);
                this.visible = true;
                Serial.xBetController.buttonsVisible = false;
                Serial.background.setMode(Serial.model.data.nextMode);
                this.playIntroSound();
            },
            gsap.to(this._text, {alpha : 1, duration : restore ? 0 : 0.8})
        ]);
        tl.add(() => {this._spine.state.setAnimation(0, "Loop", true);},
            this._spine.spineData.findAnimation("Start").duration
        );
        tl.add(() => super.show(onContinueCallBack));
    }

    private playIntroSound():void {
        const soundName = Serial.model.data.nextMode.includes("SUPER") ? SerialSound.SFS_INTRO_LOOP : SerialSound.FS_INTRO_LOOP;
        SerialSound.soundPlayer.playTrack({
            name : soundName,
            repeat : 1000
        });
        SerialSound.PLAYING_LOOP_EFFECT = soundName;
        SerialSound.fadeMusic(0, 1);
    }

    protected close():gsap.core.Timeline {
        const tl = super.close();
        tl.add(
            () => {
                if(!Serial.model.isRestore) {
                    this.openExtraEnhancers(1);
                    this.openExtraEnhancers(2);
                    this.openExtraEnhancers(3);
                }
                SerialSound.soundPlayer.playTrack({name : SerialSound.CONTINUE_FS_INTRO});
                this.startFSMusic();
                this._dimmerLooping = false;
                this.stopDimmerLoop();
                this._text.alpha = 0;
            }, 0);
        tl.add(() => {
            this._spine.state.clearTrack(0);
            this._spine.skeleton.setToSetupPose();
            this._spine.state.setEmptyAnimation(0, 0);
            this._spine.visible = false;
            this._spine.parent.removeChild(this._spine);
            this._spine.destroy();
            if(Serial.model.data.nextMode.includes("SUPER")) {
                this._spineBg.state.clearTrack(0);
                this._spineBg.skeleton.setToSetupPose();
                this._spineBg.state.setEmptyAnimation(0, 0);
                this._spineBg.visible = false;
                this._spineBg.parent.removeChild(this._spineBg);
                this._spineBg.destroy();
            }
        }, 0.01);
        return tl;
    }

    private openExtraEnhancers(reelId:number):void {
        (<HiddenEnhancerReelController>Serial.enhancerReels[reelId][1]).open();
        (<HiddenEnhancerReelController>Serial.enhancerReels[reelId][5]).open();
        Serial.reelPositionControls[reelId].arrange();
    }

    private startFSMusic():void {
        SerialSound.PLAYING_LOOP_EFFECT = "";
        SerialSound.soundPlayer.stopTrack(SerialSound.FS_INTRO_LOOP);
        SerialSound.soundPlayer.stopTrack(SerialSound.SFS_INTRO_LOOP);
        SerialSound.soundPlayer.playTrack({name : Serial.model.data.nextMode.includes("SUPER") ? SerialSound.PHONE_INTRO_FS_SUPER : SerialSound.PHONE_INTRO_FS});
        SerialSound.switchMusic({
            name : Serial.model.data.nextMode.includes("SUPER") ? SerialSound.FREESPIN_SUPER_MUSIC : SerialSound.FREESPIN_MUSIC,
            volume : 1
        });
    }

    private playDimmerLoop():void {
        if(!this._dimmerLooping) {
            return;
        }

        this._dimmerTl = gsap.timeline();
        this._dimmerTl.add(Serial.background.tweenDimmer(0.3, 1.8));
        this._dimmerTl.add(Serial.background.tweenDimmer(0.7, 2.2));
        this._dimmerTl.add(() => {this.playDimmerLoop()});
    }

    private stopDimmerLoop():void {
        if(this._dimmerTl && this._dimmerTl.isActive()) {
            this._dimmerTl.pause();
            this._dimmerTl.kill();
        }
        this._dimmerTl = null;
        Serial.background.tweenDimmer(0, 0.1);
    }
}